"use client";

import type React from "react";
import { Box, type BoxElement, type BoxProps } from "./box";
import {
  RING_HIGHLIGHT,
  RING_RADIUS,
  type RoundedToken,
} from "./box-classes";
import { Text, type TextElement, type TextProps } from "./text";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * Frame is a tray that holds panels.
 *
 * The difference from `Card` is where the seam lives. A Card is ONE bordered
 * surface and its parts (`CardHeader`/`CardPanel`/`CardFooter`) are regions
 * inside that one border. A Frame paints no content surface at all: it is a
 * recessed tray, and each `FramePanel` inside it is its own bordered panel
 * floating on that tray. Two panels are therefore separated by the tray's own
 * fill showing through the gap between them, not by a hairline divider — which
 * is why grouped settings, grouped rows, and a stack of related regions read as
 * distinct objects here and as one object in a Card.
 *
 * `FrameHeader`/`FrameFooter` sit ON the tray, outside any panel. That is the
 * point: a header that labels the whole group must not live inside one of the
 * things it labels.
 *
 *   ┌─ Frame (muted tray) ───────────────┐
 *   │  FrameHeader — title, description  │
 *   │  ┌─ FramePanel (card) ──────────┐   │
 *   │  └──────────────────────────────┘   │
 *   │  ┌─ FramePanel (card) ──────────┐   │
 *   │  └──────────────────────────────┘   │
 *   │  FrameFooter — actions             │
 *   └────────────────────────────────────┘
 *
 * Every part is a `Box`, so layout, spacing, `surface` and `resize` are the
 * ones Box already defines. Each default below is a Box prop rather than a
 * baked class, so a call site overrides it with the same prop instead of a
 * `className`.
 */

/**
 * Separation between adjacent panels, matching the tray's own inset so a panel
 * is the same distance from its neighbour as from the tray edge. Written as a
 * whole arbitrary variant because Tailwind only emits classes it can read
 * verbatim in source.
 */
const FRAME_PANEL_GAP =
  "[&>[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-1";

export type FrameProps<T extends BoxElement = "div"> = BoxProps<T>;

export function Frame<T extends BoxElement = "div">({
  className,
  ...props
}: FrameProps<T>): React.ReactElement {
  return (
    <Box
      background="muted"
      className={cn(FRAME_PANEL_GAP, className)}
      direction="column"
      padding="xs"
      rounded="2xl"
      {...props}
      data-slot="frame"
    />
  );
}

export type FramePanelProps<T extends BoxElement = "div"> =
  BoxProps<T> & Omittable<{
    /** Corner rounding. Drives the outer corner and the inner highlight ring
     *  together — see `RING_RADIUS`. */
    radius?: RoundedToken;
  }>;

/**
 * One bordered panel resting on the tray. `surface="card"` by default: in
 * dark mode `card` is lighter than the tray's `muted` fill, and in light
 * mode — where `background` and `card` share one fill — the border and the
 * highlight ring do the separating.
 *
 * A panel in a floating tray goes the other way. Nothing floats over
 * `popover`, so a panel inside a popover-surfaced Frame passes
 * `surface="sunken"` and reads as a well.
 */
export function FramePanel<T extends BoxElement = "div">({
  className,
  radius = "xl",
  surface,
  ...props
}: FramePanelProps<T>): React.ReactElement {
  return (
    <Box
      className={cn(
        // A panel that NAMES a surface takes that surface's fill, hairline and
        // shadow from Box and adds only the border WIDTH here. Emitting the
        // default fill alongside would defeat the named surface, because Box
        // merges `className` last.
        surface ? "border" : "border bg-card shadow-xs/5",
        "not-dark:bg-clip-padding",
        RING_HIGHLIGHT,
        RING_RADIUS[radius],
        className,
      )}
      direction="column"
      padding="md"
      surface={surface ?? "card"}
      {...props}
      data-slot="frame-panel"
    />
  );
}

export type FrameHeaderProps<T extends BoxElement = "header"> =
  BoxProps<T>;

/** Labels the whole group. Sits on the tray, outside every panel. */
export function FrameHeader<T extends BoxElement = "header">(
  props: FrameHeaderProps<T>,
): React.ReactElement {
  return (
    <Box
      as="header"
      direction="column"
      paddingX="md"
      paddingY="base"
      {...props}
      data-slot="frame-header"
    />
  );
}

export function FrameTitle<T extends TextElement = "span">({
  ...props
}: TextProps<T>): React.ReactElement {
  return (
    <Text
      size="sm"
      weight="semibold"
      {...props}
      data-slot="frame-title"
    />
  );
}

export function FrameDescription<T extends TextElement = "span">({
  ...props
}: TextProps<T>): React.ReactElement {
  return (
    <Text size="sm" variant="muted" {...props} data-slot="frame-description" />
  );
}

export type FrameFooterProps<T extends BoxElement = "footer"> =
  BoxProps<T>;

/** Actions for the whole group. Sits on the tray, outside every panel. */
export function FrameFooter<T extends BoxElement = "footer">(
  props: FrameFooterProps<T>,
): React.ReactElement {
  return (
    <Box
      align="center"
      as="footer"
      direction="row"
      paddingX="md"
      paddingY="base"
      {...props}
      data-slot="frame-footer"
    />
  );
}
